import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

function About() {
  const values = [
    {
      number: "01",
      title: "Joy in the everyday",
      text: "Useful objects can also feel generous, surprising and full of character.",
    },
    {
      number: "02",
      title: "Made with intention",
      text: "Every colour, proportion and finish is considered for a long life at home.",
    },
    {
      number: "03",
      title: "Hands and heritage",
      text: "We value the knowledge of makers and the variations that give pieces soul.",
    },
    {
      number: "04",
      title: "A lighter footprint",
      text: "Thoughtful runs, durable materials and minimal packaging guide our growth.",
    },
  ];

  const collageImages = [
    {
      className: "about-collage__item--one",
      src: "/main/ottireluna/studio-shelves.jpg",
      alt: "Shelves of glassware and ceramics in our studio",
    },
    {
      className: "about-collage__item--two",
      src: "/main/ottireluna/vase-window.jpg",
      alt: "Ribbed vase beside books on a bright windowsill",
    },
    {
      className: "about-collage__item--three",
      src: "/main/ottireluna/linen-table.jpg",
      alt: "A calm dining table with linen and glassware",
    },
    {
      className: "about-collage__item--four",
      src: "/main/ottireluna/evening-lantern.jpg",
      alt: "A glass candle lantern glowing on a side table",
    },
  ];

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="about-page">
      <header className="about-intro" aria-labelledby="about-title">
        <p className="about-eyebrow">{siteConfig.location}</p>
        <h1 id="about-title">Our Story</h1>
        <p className="about-intro__copy">
          {siteConfig.description} We draw on the optimism, craft and easy rhythm
          of thoughtful urban living.
        </p>
      </header>

      <section className="about-collage" aria-label="Objects from our collection">
        {collageImages.map((image) => (
          <figure
            className={`about-collage__item ${image.className}`}
            key={image.src}
          >
            <img src={image.src} alt={image.alt} loading="lazy" decoding="async" />
          </figure>
        ))}
      </section>

      <section className="about-slogan" aria-labelledby="about-slogan-title">
        <p className="about-eyebrow">{siteConfig.tagline}</p>
        <h2 id="about-slogan-title">
          We select objects that bring warmth, texture and colour into
          everyday spaces.
        </h2>
      </section>

      <section className="about-chapter" id="our-story">
        <figure className="about-chapter__image">
          <img
            src="/main/ottireluna/craft-hands.jpg"
            alt={`${siteConfig.brandName} craft process in the studio`}
            loading="lazy"
            decoding="async"
          />
          <figcaption>At the studio · {siteConfig.contact.address}</figcaption>
        </figure>
        <div className="about-chapter__copy">
          <p className="about-eyebrow">A Hong Kong point of view</p>
          <h2>Useful pieces, chosen for real homes.</h2>
          <p>
            From Hong Kong, we look for enduring ideas: practical forms, honest
            materials and the pleasure of gathering around beautiful things.
          </p>
          <p>
            Each collection begins with drawing, material experiments and a
            question: can a familiar object bring a little more joy to a room?
          </p>
        </div>
      </section>

      <section className="about-chapter about-chapter--reverse">
        <div className="about-chapter__copy">
          <p className="about-eyebrow">Ideas shaped by hands</p>
          <h2>Designed slowly. Enjoyed every day.</h2>
          <p>
            We work in close dialogue with craftspeople and specialist production
            partners, balancing traditional knowledge with a crisp, modern point
            of view. Natural textures and confident palettes make every piece
            distinctive, while careful construction keeps it practical.
          </p>
          <p>
            The small variations of the making process are welcomed. They are a
            reminder that an object has travelled through attentive hands before
            finding its place in yours.
          </p>
        </div>
        <figure className="about-chapter__image">
          <img
            src="/main/ottireluna/salad-table.jpg"
            alt="Acacia wood servers and linen on a pale table"
            loading="lazy"
            decoding="async"
          />
          <figcaption>Colour, material and the mark of the maker.</figcaption>
        </figure>
      </section>

      <section className="about-quote" aria-label="Our design philosophy">
        <blockquote>
          “A home should hold memories, colour and a little bit of sunshine.”
        </blockquote>
        <p>{siteConfig.brandName} studio philosophy</p>
      </section>

      <section className="about-values" aria-labelledby="about-values-title">
        <div className="about-values__heading">
          <p className="about-eyebrow">What guides us</p>
          <h2 id="about-values-title">The values behind every object.</h2>
        </div>
        <div className="about-values__grid">
          {values.map((value) => (
            <article className="about-value" key={value.number}>
              <span>{value.number}</span>
              <h3>{value.title}</h3>
              <p>{value.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-cta" aria-labelledby="about-cta-title">
        <p className="about-eyebrow">Bring warmth home</p>
        <h2 id="about-cta-title">Find an object that makes your room smile.</h2>
        <Link className="about-cta__button" to="/shop" onClick={toTop}>
          Explore the collection
        </Link>
      </section>
    </main>
  );
}

export default About;
